// Coerce C# `long` values (serialized as bigint | string by openapi-ts) to a plain number.
// Use for arithmetic / comparisons on count fields like deviceCount, totalCount, fileSystemUsage.total.
export function toNumber(value: number | string | bigint | null | undefined): number {
	if (value == null) return 0
	if (typeof value === "number") return value
	if (typeof value === "bigint") return Number(value)
	const parsed = Number(value)
	return Number.isNaN(parsed) ? 0 : parsed
}

export function urlB64ToUint8Array(base64String: string) {
	const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
	const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")

	const rawData = window.atob(base64)
	const outputArray = new Uint8Array(rawData.length)

	for (let i = 0; i < rawData.length; ++i) {
		outputArray[i] = rawData.charCodeAt(i)
	}
	return outputArray
}

export async function promiseRetry<T>(
	fn: () => Promise<T>,
	retries: number,
	retryIntervalMillis: number,
	previousError?: Error
): Promise<T> {
	return !retries
		? Promise.reject(previousError)
		: fn().catch(async (error) => {
				await new Promise((resolve) => setTimeout(resolve, retryIntervalMillis))
				return promiseRetry(fn, retries - 1, retryIntervalMillis, error)
			})
}

export const nestedSort = <T>(array: T[], sortBy: string, order: "asc" | "desc"): T[] => {
	const getNestedValue = <U>(obj: U, path: string) => {
		return path.split(".").reduce(
			(acc: object | undefined, part: string) => {
				if (acc && typeof acc === "object") {
					return acc[part as keyof typeof acc]
				}
				return undefined
			},
			obj as object | undefined
		)
	}

	return array?.sort((a, b) => {
		const orderDirection = order === "asc" ? 1 : -1

		const valueA = getNestedValue(a, sortBy as string)
		const valueB = getNestedValue(b, sortBy as string)

		if (valueA && valueB) {
			if (valueA < valueB) return -1 * orderDirection
			if (valueA > valueB) return 1 * orderDirection
		}
		return 0
	})
}

export const isBase64 = (str: string): boolean => {
	try {
		return btoa(atob(str)) === str
	} catch (err) {
		console.error(`error while checking for base64 format : ${err}`)
		return false
	}
}

export function hasProp<T extends string>(o: unknown, prop: T): o is Record<T, unknown> {
	return typeof o === "object" && o !== null && prop in o
}
